import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import { type Table } from "@tanstack/react-table";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMemo } from "react";

export interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  pageSizeOptions?: number[];
  getCanPreviousPage?: () => boolean;
  getCanNextPage?: () => boolean;
  onPageSizeChange?: (pageSize: number) => void;
  onPageChange?: (pageIndex: number) => void;
}

export function DataTablePagination<TData>({
  table,
  pageSizeOptions = [10, 20, 30, 50, 100, 500, 1000, 2000],
  getCanPreviousPage,
  getCanNextPage,
  onPageSizeChange,
  onPageChange,
}: DataTablePaginationProps<TData>) {
  const currentPageSize = table.getState().pagination.pageSize;
  const currentPageIndex = table.getState().pagination.pageIndex;

  const allPageSizeOptions = useMemo(() => {
    if (!pageSizeOptions.includes(currentPageSize)) {
      return [...pageSizeOptions, currentPageSize].sort((a, b) => a - b);
    }
    return pageSizeOptions;
  }, [pageSizeOptions, currentPageSize]);

  const handlePreviousPage = () => {
    if (onPageChange) {
      onPageChange(currentPageIndex - 1);
    } else {
      table.previousPage();
    }
  };

  const handleNextPage = () => {
    if (onPageChange) {
      onPageChange(currentPageIndex + 1);
    } else {
      table.nextPage();
    }
  };

  const handlePageSizeChange = (value: string) => {
    const newPageSize = Number(value);
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
    } else {
      table.setPageSize(newPageSize);
    }
  };

  const canPreviousPage = getCanPreviousPage
    ? getCanPreviousPage()
    : table.getCanPreviousPage();
  const canNextPage = getCanNextPage
    ? getCanNextPage()
    : table.getCanNextPage();

  return (
    <div className="flex w-full flex-col-reverse items-center justify-between gap-4 overflow-auto p-1 sm:flex-row sm:gap-8">
      <div className="flex-1 whitespace-nowrap text-sm text-muted-foreground"></div>
      <div className="flex flex-col-reverse items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8">
        <div className="flex items-center space-x-2">
          <p className="whitespace-nowrap text-sm font-medium">Rows per page</p>
          <Select
            value={`${currentPageSize}`}
            onValueChange={handlePageSizeChange}
          >
            <SelectTrigger className="h-8 w-[5rem]">
              <SelectValue placeholder={currentPageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {allPageSizeOptions.map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {table.getPageCount() === -1 ? (
          <div className="flex items-center justify-center text-sm font-medium">
            Page {currentPageIndex + 1}
          </div>
        ) : (
          <div className="flex items-center justify-center text-sm font-medium">
            Page {currentPageIndex + 1} of {table.getPageCount()}
          </div>
        )}
        <div className="flex items-center space-x-2">
          <Button
            aria-label="Go to previous page"
            variant="outline"
            size="icon"
            className="size-8"
            onClick={handlePreviousPage}
            disabled={!canPreviousPage}
          >
            <ChevronLeftIcon className="size-4" aria-hidden="true" />
          </Button>
          <Button
            aria-label="Go to next page"
            variant="outline"
            size="icon"
            className="size-8"
            onClick={handleNextPage}
            disabled={!canNextPage}
          >
            <ChevronRightIcon className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}
